import {
  createEffect,
  createSignal,
  ErrorBoundary,
  on,
  onCleanup,
  onMount,
  untrack,
} from "solid-js";
import { msg } from "@lingui/core/macro";
import { Outlet, useLocation, useNavigate, useRouterState } from "@tanstack/solid-router";
import type { AbstractPowerSyncDatabase } from "@powersync/common";

import { appState } from "@fleet/ui";
import { useTheme } from "@/providers/theme-provider";
import { useI18n } from "@/providers/i18n-provider";
import { useSearchPalette } from "@/features/search/hooks/use-search-palette";

// Layout specific components and providers
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { ShortcutsModal } from "@/components/shortcuts-modal";
import { ErrorBoundaryFallback } from "@/components/ui/error-boundary-fallback";
import { CommandPalette } from "@/features/search/components/command-palette";

// Layout module parts
import { useInteractionFlag } from "@fleet/ui";
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";
import { useSession } from "@/lib/auth";
import { useUndo } from "@/lib/undo";

interface AppLayoutContentProps {
  db: AbstractPowerSyncDatabase | null;
}

function useGlobalEventListeners(
  isSidebarOpen: () => boolean,
  setIsSidebarOpen: (open: boolean) => void,
) {
  // Keyboard-vs-pointer activity tracking (and the `body.keyboard-navigation`
  // class) is owned by <InteractionProvider>; only the sidebar-specific Escape
  // handler remains here.
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };

    globalThis.addEventListener("keydown", handleEscapeForSidebar);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

export function AppLayoutContent(_props: AppLayoutContentProps) {
  onMount(() => {
    // Notify the InitialSyncOverlay that we are mounted and ready
    appState.markAsReady();
  });

  const navigate = useNavigate();
  const searchPalette = useSearchPalette();
  const { t } = useI18n();

  const handleCloseSearch = () => {
    searchPalette.close();
  };

  const location = useLocation();
  const routerState = useRouterState();
  const isNavigating = () => routerState().status === "pending";

  // ── Route entrance animation ────────────────────────────────────────────
  // Plays the canonical `slide-in-up` once per navigation on a dedicated
  // wrapper around the <Outlet>, keyed by pathname (search params excluded so
  // in-place filters/queries don't re-trigger it). The wrapper is SEPARATE from
  // any element that receives swipe transforms, and we clear `will-change` on
  // `animationend` so the layer never leaves behind a containing block that
  // would break SwipeRow/SwipeTableRow translate3d gestures.
  let entranceRef: HTMLDivElement | undefined = undefined;
  const handleEntranceEnd = (e: AnimationEvent) => {
    if (e.target === entranceRef) {
      entranceRef?.style.removeProperty("will-change");
    }
  };
  createEffect(() => {
    const { pathname } = location();
    const el = entranceRef;
    if (!el) {
      return;
    }
    // Replay the CSS animation on every pathname change: strip the class,
    // force reflow, then re-add it. `will-change` is re-armed for the new run.
    el.classList.remove("route-entrance");
    void el.offsetWidth; // reflow → animation restarts
    el.style.willChange = "transform, opacity";
    el.classList.add("route-entrance");
    void pathname;
  });

  const theme = useTheme();

  const [isSidebarOpen, setIsSidebarOpen] = createSignal(false);
  const [shortcutsOpen, setShortcutsOpen] = createSignal(false);

  // Holds the active ErrorBoundary reset fn so we can clear a stuck error
  // automatically when the user navigates to a different route.
  const [boundaryReset, setBoundaryReset] = createSignal<(() => void) | null>(null);

  // Close the mobile sidebar AND reset any error boundary whenever the route
  // changes.
  createEffect(() => {
    void location().pathname;
    setIsSidebarOpen(false);
    // Read the reset fn untracked so this effect only fires on navigation, not
    // when the boundary registers its reset during a fallback render.
    const reset = untrack(boundaryReset);
    if (reset) {
      reset();
      setBoundaryReset(null);
    }
  });

  // ── Undo L1 (ACTIVITY-CORE): per-account stack + Ctrl/Cmd+Z ──────────────
  const { userId } = useSession();
  const { undoLast, clearUndoStack } = useUndo();
  // The undo stack belongs to the ACTIVE ACCOUNT: it is cleared when changing accounts
  // (defer: do not clear on the initial mount).
  createEffect(
    on(
      userId,
      () => {
        clearUndoStack();
      },
      { defer: true },
    ),
  );

  useAppShortcuts({
    searchPalette,
    navigate,
    theme,
    shortcutsOpen,
    setShortcutsOpen,
    setIsSidebarOpen,
    undoLast,
  });

  useGlobalEventListeners(isSidebarOpen, setIsSidebarOpen);

  // With the nav drawer open, the horizontal gesture belongs to it (drag to
  // dismiss). Any future page-level swipe would listen on the window and
  // otherwise fire underneath it, so ownership is announced in the
  // interaction context rather than re-derived from the DOM by each gesture.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  return (
    <>
      {/* Skip-to-content: first focusable element, visible only on keyboard focus. */}
      <a
        href="#main-content"
        class="sr-only focus-visible:not-sr-only focus-visible:fixed focus-visible:left-4 focus-visible:top-4 focus-visible:z-[400] focus-visible:rounded-lg focus-visible:bg-primary focus-visible:px-4 focus-visible:py-2 focus-visible:text-sm focus-visible:font-semibold focus-visible:text-primary-foreground focus-visible:shadow-lg"
      >
        {t(msg`Skip to content`)}
      </a>
      <AppBackground
        class="flex h-dvh text-foreground overflow-hidden"
        isBlur={theme.isBlur()}
        background={theme.tokens()?.background}
        patternType={theme.tokens()?.patternType}
        patternOpacity={theme.tokens()?.patternOpacity}
        glow0={theme.tokens()?.glow0}
        glow1={theme.tokens()?.glow1}
        glow2={theme.tokens()?.glow2}
      >
        <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} />

        {/* ── Main content ───────────────────────────────────────── */}
        <main
          id="main-content"
          class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)]"
        >
          <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
          <OfflineBanner />
          <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
            <div class="flex flex-1 flex-col min-h-0 h-full">
              {/*
                Global render-error net: any uncaught throw in a route body
                degrades to a premium full-region fallback (illustration +
                Retry + Reload) instead of a blank screen. The boundary is
                auto-reset on navigation (see createEffect on `boundaryReset`),
                so an error on one route never sticks after leaving it.
              */}
              <ErrorBoundary
                fallback={(error: unknown, reset: () => void) => {
                  setBoundaryReset(() => reset);
                  return <ErrorBoundaryFallback error={error} reset={reset} />;
                }}
              >
                <div
                  ref={(el) => {
                    entranceRef = el;
                  }}
                  class="route-entrance flex flex-1 flex-col min-h-0 h-full"
                  onAnimationEnd={handleEntranceEnd}
                >
                  <Outlet />
                </div>
              </ErrorBoundary>
            </div>
          </div>
        </main>

        <ShortcutsModal
          isOpen={shortcutsOpen()}
          onClose={() => {
            setShortcutsOpen(false);
          }}
        />
      </AppBackground>

      {/* Command Palette — rendered OUTSIDE AppBackground to avoid blur compositor flash */}
      <CommandPalette isOpen={searchPalette.isOpen()} onClose={handleCloseSearch} />
    </>
  );
}
